
import { z } from 'zod';

/**
 * Enhanced security validation schemas with comprehensive protection
 */
export const secureValidationSchemas = {
  // Strong password validation with comprehensive requirements
  strongPassword: z
    .string()
    .min(12, 'Password must be at least 12 characters')
    .max(128, 'Password too long')
    .refine(
      (password) => /[A-Z]/.test(password),
      'Password must contain at least one uppercase letter'
    )
    .refine(
      (password) => /[a-z]/.test(password),
      'Password must contain at least one lowercase letter'
    )
    .refine(
      (password) => /[0-9]/.test(password),
      'Password must contain at least one number'
    )
    .refine(
      (password) => /[^A-Za-z0-9]/.test(password),
      'Password must contain at least one special character'
    )
    .refine(
      (password) => !/(.)\1{2,}/.test(password),
      'Password cannot contain repeated characters (3+ times)'
    )
    .refine(
      (password) => !/^.*(password|123456|qwerty|admin).*$/i.test(password),
      'Password cannot contain common words'
    ),

  // Email validation with enhanced security checks
  secureEmail: z
    .string()
    .email('Invalid email address')
    .max(254, 'Email address too long')
    .refine(
      (email) => !email.includes('..'),
      'Email contains invalid characters'
    )
    .refine(
      (email) => !/[<>"]/.test(email),
      'Email contains invalid characters'
    )
    .refine(
      (email) => !/javascript:/gi.test(email),
      'JavaScript URLs are not allowed'
    )
    .refine(
      (email) => !email.includes('\0'),
      'Null bytes are not allowed'
    ),

  // Sanitized text input with XSS protection
  sanitizedText: z
    .string()
    .max(1000, 'Text too long')
    .transform((text) => text.trim())
    .refine(
      (text) => !/<script[^>]*>.*?<\/script>/gi.test(text),
      'Script tags are not allowed'
    )
    .refine(
      (text) => !/javascript:/gi.test(text),
      'JavaScript URLs are not allowed'
    )
    .refine(
      (text) => !/on\w+\s*=/gi.test(text),
      'Event handlers are not allowed'
    )
    .refine(
      (text) => !text.includes('\0'),
      'Null bytes are not allowed'
    )
    .transform((text) => {
      // Basic HTML entity encoding for common XSS vectors
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;');
    }),

  // Secure name validation
  secureName: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name too long')
    .transform((name) => name.trim())
    .refine(
      (name) => /^[a-zA-Z\s\-'\.]+$/.test(name),
      'Name contains invalid characters'
    )
    .refine(
      (name) => !name.includes('\0'),
      'Null bytes are not allowed'
    ),

  // Secure phone number validation
  securePhone: z
    .string()
    .max(20, 'Phone number too long')
    .transform((phone) => phone.trim())
    .refine(
      (phone) => /^[\+]?[1-9][\d\s\-\(\)\.]{7,}$/.test(phone) || phone === '',
      'Invalid phone number format'
    )
    .refine(
      (phone) => !phone.includes('\0'),
      'Null bytes are not allowed'
    )
    .optional(),

  // Secure URL validation
  secureUrl: z
    .string()
    .url('Invalid URL')
    .max(2048, 'URL too long')
    .refine(
      (url) => /^https?:\/\//.test(url),
      'Only HTTP and HTTPS URLs are allowed'
    )
    .refine(
      (url) => !url.includes('javascript:'),
      'JavaScript URLs are not allowed'
    )
    .refine(
      (url) => !url.includes('\0'),
      'Null bytes are not allowed'
    )
    .optional(),

  // Secure file name validation
  secureFileName: z
    .string()
    .max(255, 'File name too long')
    .transform((name) => name.trim())
    .refine(
      (name) => !/[<>:"/\\|?*\0]/.test(name),
      'File name contains invalid characters'
    )
    .refine(
      (name) => !name.startsWith('.'),
      'File name cannot start with a dot'
    )
    .refine(
      (name) => !/\.(exe|bat|cmd|scr|pif|com)$/i.test(name),
      'Executable file types are not allowed'
    ),
};

/**
 * Enhanced candidate form validation with security
 */
export const secureCandidateFormValidation = z.object({
  first_name: secureValidationSchemas.secureName,
  last_name: secureValidationSchemas.secureName,
  email: secureValidationSchemas.secureEmail, // Made required (removed .optional())
  phone: secureValidationSchemas.securePhone.or(z.literal('')),
  source: secureValidationSchemas.sanitizedText.optional().or(z.literal('')),
  status: z.string().optional(),
  job_id: z.string().uuid().optional().or(z.literal('')), // Added job selection
  linkedin_url: secureValidationSchemas.secureUrl.or(z.literal('')),
  notes: secureValidationSchemas.sanitizedText.optional().or(z.literal('')),
  skills: z.array(secureValidationSchemas.sanitizedText).optional(),
});

/**
 * Enhanced email content validation with security
 */
export const secureEmailContentValidation = z.object({
  subject: z.string()
    .max(998, 'Subject line too long')
    .transform((subject) => subject.trim())
    .refine((subject) => !subject.includes('\0'), 'Null bytes are not allowed'),
  body: secureValidationSchemas.sanitizedText
    .refine((body) => body.length <= 100000, 'Email body too long'),
  to: z.array(secureValidationSchemas.secureEmail),
  cc: z.array(secureValidationSchemas.secureEmail).optional(),
  bcc: z.array(secureValidationSchemas.secureEmail).optional(),
});

/**
 * Enhanced authentication validation
 */
export const secureAuthValidation = z.object({
  email: secureValidationSchemas.secureEmail,
  password: secureValidationSchemas.strongPassword,
});

/**
 * Rate limiting validation for security-sensitive operations
 */
export const createRateLimitKey = (operation: string, identifier: string): string => {
  return `rate_limit:${operation}:${identifier}`;
};

/**
 * Input sanitization utility
 */
export const sanitizeInput = (input: string): string => {
  return secureValidationSchemas.sanitizedText.parse(input);
};
